'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import SortableContentCard from './cards/SortableContentCard';
import AiTextTTS from './ai-text/AiTextTTS';
import LinkBoard from './LinkBoard';
import { safeRandomUUID } from '@/lib/uuid';
import {
  fetchAllGeneratedTexts,
  updateGeneratedTextRequest,
  updateGeneratedTextOrderRequest,
  deleteGeneratedTextRequest,
  type GeneratedTextItem,
} from '@/lib/generatedTexts';

interface Memo {
  id: string;
  title: string;
  content: string;
  sort_order: number;
  created_at: number;
  updated_at: number;
}

type FontSize = 'sm' | 'base' | 'lg' | 'xl';
const FONT_SIZE_REM: Record<FontSize, string> = {
  sm: '0.875rem',
  base: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
};
const FONT_SIZE_LABEL: Record<FontSize, string> = {
  sm: 'S',
  base: 'M',
  lg: 'L',
  xl: 'XL',
};

type View = 'memo' | 'generated' | 'links';
const VIEWS: { key: View; label: string }[] = [
  { key: 'memo', label: 'Memo' },
  { key: 'generated', label: 'Generated' },
  { key: 'links', label: 'Links' },
];

export default function MemoBoard() {
  const [memos, setMemos] = useState<Memo[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [view, setView] = useState<View>(() => {
    if (typeof window === 'undefined') return 'memo';
    try {
      const v = localStorage.getItem('memo-board-view');
      return v === 'generated' || v === 'links' ? v : 'memo';
    } catch { return 'memo'; }
  });
  const [generated, setGenerated] = useState<GeneratedTextItem[]>([]);
  const [genLoading, setGenLoading] = useState(false);
  const [fontSize, setFontSize] = useState<FontSize>(() => {
    if (typeof window === 'undefined') return 'sm';
    try {
      const v = localStorage.getItem('memo-board-font-size');
      if (v === 'sm' || v === 'base' || v === 'lg' || v === 'xl') return v;
    } catch { /* ignore */ }
    return 'sm';
  });

  useEffect(() => {
    try { localStorage.setItem('memo-board-view', view); } catch { /* ignore */ }
  }, [view]);

  useEffect(() => {
    try { localStorage.setItem('memo-board-font-size', fontSize); } catch { /* ignore */ }
  }, [fontSize]);

  useEffect(() => {
    if (view !== 'generated') return;
    setGenLoading(true);
    fetchAllGeneratedTexts()
      .then(setGenerated)
      .finally(() => setGenLoading(false));
  }, [view]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  // Fetch memos
  useEffect(() => {
    fetch('/api/memos')
      .then((res) => res.json())
      .then((data) => {
        setMemos(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Add new memo
  const addMemo = useCallback(async () => {
    const id = safeRandomUUID();
    const sortOrder = memos.length;
    try {
      const res = await fetch('/api/memos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, content: '', sortOrder }),
      });
      const newMemo = await res.json();
      setMemos((prev) => [...prev, newMemo]);
    } catch (err) {
      console.error('Failed to create memo:', err);
    }
  }, [memos.length]);

  // Update memo content
  const updateMemo = useCallback(async (id: string, title: string, content: string) => {
    // Auto-generate title from first line if title is empty and content exists
    const autoTitle = !title && content ? content.split('\n')[0].slice(0, 50) : title;
    setMemos((prev) =>
      prev.map((m) => (m.id === id ? { ...m, title: autoTitle, content, updated_at: Date.now() } : m))
    );
    try {
      await fetch('/api/memos', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, title: autoTitle, content }),
      });
    } catch (err) {
      console.error('Failed to update memo:', err);
    }
  }, []);

  // Delete memo
  const deleteMemo = useCallback(async (id: string) => {
    setMemos((prev) => prev.filter((m) => m.id !== id));
    try {
      await fetch('/api/memos', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
    } catch (err) {
      console.error('Failed to delete memo:', err);
    }
  }, []);

  const updateGenerated = useCallback(async (id: string, title: string, content: string) => {
    const autoTitle = !title && content ? content.split('\n')[0].slice(0, 50) : title;
    setGenerated((prev) =>
      prev.map((g) => (g.id === id ? { ...g, title: autoTitle, content, updated_at: Date.now() } : g))
    );
    await updateGeneratedTextRequest(id, autoTitle, content);
  }, []);

  const deleteGenerated = useCallback(async (id: string) => {
    setGenerated((prev) => prev.filter((g) => g.id !== id));
    await deleteGeneratedTextRequest(id);
  }, []);

  const handleGeneratedDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = generated.findIndex((g) => g.id === active.id);
      const newIndex = generated.findIndex((g) => g.id === over.id);
      const reordered = arrayMove(generated, oldIndex, newIndex);
      setGenerated(reordered);
      const orders = reordered.map((g, i) => ({ id: g.id, sort_order: i }));
      await updateGeneratedTextOrderRequest(orders);
    },
    [generated]
  );

  // Drag & drop
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = memos.findIndex((m) => m.id === active.id);
      const newIndex = memos.findIndex((m) => m.id === over.id);
      const reordered = arrayMove(memos, oldIndex, newIndex);
      setMemos(reordered);

      const orders = reordered.map((m, i) => ({ id: m.id, sort_order: i }));
      try {
        await fetch('/api/memos', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orders }),
        });
      } catch (err) {
        console.error('Failed to update order:', err);
      }
    },
    [memos]
  );

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-[var(--text-muted)]">
        読み込み中...
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-4 md:px-6 py-3 border-b border-[var(--border)]">
        <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-wrap">
          <h1 className="text-lg font-semibold text-[var(--text-primary)]">Memo</h1>
          <div className="flex items-center gap-1 p-0.5 rounded-md bg-[var(--bg-secondary)] border border-[var(--border)]">
            {VIEWS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setView(key)}
                className={`px-2.5 py-0.5 rounded text-xs transition-colors ${
                  view === key
                    ? 'bg-[var(--accent)] text-white'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {/* Font size selector (カード本文にしか効かないので Links では出さない) */}
          {view !== 'links' && (
            <div
              className="flex items-center gap-0.5 p-0.5 rounded-md bg-[var(--bg-secondary)] border border-[var(--border)]"
              title="本文のフォントサイズ"
            >
              {(['sm', 'base', 'lg', 'xl'] as FontSize[]).map(size => (
                <button
                  key={size}
                  onClick={() => setFontSize(size)}
                  className={`px-2 py-0.5 rounded text-xs transition-colors ${
                    fontSize === size
                      ? 'bg-[var(--accent)] text-white'
                      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                  }`}
                  aria-label={`フォントサイズ ${FONT_SIZE_LABEL[size]}`}
                >
                  {FONT_SIZE_LABEL[size]}
                </button>
              ))}
            </div>
          )}
        </div>
        {view === 'memo' && (
          <button
            onClick={addMemo}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--accent)] text-white text-sm hover:bg-[var(--accent-hover)] transition-colors"
          >
            <span className="text-base leading-none">+</span>
            追加
          </button>
        )}
      </div>

      {/* Masonry board */}
      {view === 'links' ? (
        // Links は自前でツールバー (検索/編集) を持つので、スクロール枠の外に出す。
        <LinkBoard />
      ) : (
      <div
        className="flex-1 overflow-y-auto p-4 md:p-6"
        style={{ ['--card-font-size' as string]: FONT_SIZE_REM[fontSize] }}
      >
        {view === 'memo' ? (
          memos.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-[var(--text-muted)] gap-3">
              <p className="text-sm">メモがありません</p>
              <button
                onClick={addMemo}
                className="px-4 py-2 rounded-md border border-[var(--border)] text-sm hover:bg-[var(--bg-elevated)] transition-colors"
              >
                最初のメモを作成
              </button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={memos.map((m) => m.id)} strategy={rectSortingStrategy}>
                <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-3">
                  {memos.map((memo) => (
                    <SortableContentCard
                      key={memo.id}
                      id={memo.id}
                      title={memo.title}
                      content={memo.content}
                      updatedAt={memo.updated_at}
                      onUpdate={updateMemo}
                      onDelete={deleteMemo}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )
        ) : genLoading ? (
          <div className="flex items-center justify-center h-full text-[var(--text-muted)] text-sm">
            読み込み中...
          </div>
        ) : generated.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-[var(--text-muted)] gap-3">
            <p className="text-sm">まだ生成テキストがありません</p>
            <p className="text-xs">AIテキストで生成すると、ここに自動で追加されます。</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleGeneratedDragEnd}
          >
            <SortableContext items={generated.map((g) => g.id)} strategy={rectSortingStrategy}>
              <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-3">
                {generated.map((g) => (
                  <SortableContentCard
                    key={g.id}
                    id={g.id}
                    title={g.title}
                    content={g.content}
                    updatedAt={g.updated_at}
                    onUpdate={updateGenerated}
                    onDelete={deleteGenerated}
                    headerActions={<AiTextTTS text={g.content} />}
                    metadataSlot={
                      <div className="flex flex-wrap items-center gap-1 text-[10px] text-[var(--text-muted)]">
                        {g.preset_name && (
                          <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">
                            {g.preset_name}
                          </span>
                        )}
                        {(g.provider || g.model) && (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                            {[g.provider, g.model].filter(Boolean).join(' / ')}
                          </span>
                        )}
                        {g.user_input && (
                          <span
                            className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 truncate max-w-[180px]"
                            title={g.user_input}
                          >
                            指示: {g.user_input}
                          </span>
                        )}
                      </div>
                    }
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
      )}
    </div>
  );
}
